/* Reusable feedback dialog for bug reports, feature requests, and general feedback.
   Used by sidebar trigger and error boundaries. Supports prefill for crash reports. */

import { useState, useCallback } from "react";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Bug, Lightbulb, MessageSquare, CheckCircle2 } from "lucide-react";
import type { FeedbackType, FeedbackDialogProps } from "@/types/feedback";

const AUTO_CLOSE_MS = 1500;

const TAB_CONFIG: { value: FeedbackType; label: string; icon: React.ReactNode }[] = [
	{ value: "bug", label: "Bug", icon: <Bug className="size-3.5" /> },
	{ value: "feature", label: "Feature", icon: <Lightbulb className="size-3.5" /> },
	{ value: "general", label: "General", icon: <MessageSquare className="size-3.5" /> },
];

export function FeedbackDialog({ open, onOpenChange, prefill }: FeedbackDialogProps) {
	const [type, setType] = useState<FeedbackType>(prefill?.type ?? "general");
	const [title, setTitle] = useState(prefill?.title ?? "");
	const [description, setDescription] = useState(prefill?.description ?? "");
	const [submitting, setSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);

	const submitFeedback = useMutation(api.feedback.submit);

	const resetForm = useCallback(() => {
		setType(prefill?.type ?? "general");
		setTitle(prefill?.title ?? "");
		setDescription(prefill?.description ?? "");
		setSubmitting(false);
		setSubmitted(false);
	}, [prefill]);

	const handleOpenChange = (nextOpen: boolean) => {
		onOpenChange(nextOpen);
		if (!nextOpen) {
			// Small delay to allow close animation before resetting
			setTimeout(resetForm, 200);
		}
	};

	const handleSubmit = async () => {
		if (!title.trim()) return;
		setSubmitting(true);
		try {
			await submitFeedback({
				type,
				title: title.trim(),
				description: description.trim(),
				route: window.location.pathname,
				userAgent: navigator.userAgent,
				errorStack: prefill?.errorStack,
				sessionLogs: prefill?.sessionLogs,
			});
			setSubmitted(true);
			setTimeout(() => handleOpenChange(false), AUTO_CLOSE_MS);
		} catch (error) {
			console.error("[FeedbackDialog] Submit failed:", error);
			setSubmitting(false);
		}
	};

	if (submitted) {
		return (
			<Dialog open={open} onOpenChange={handleOpenChange}>
				<DialogContent className="sm:max-w-md">
					<div className="flex flex-col items-center gap-3 py-6">
						<CheckCircle2 className="size-10 text-primary" />
						<p className="text-lg font-medium">Thank you!</p>
						<p className="text-sm text-muted-foreground">Your feedback has been submitted.</p>
					</div>
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Send Feedback</DialogTitle>
					<DialogDescription>
						Let us know about bugs, feature ideas, or anything else.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<Tabs value={type} onValueChange={(v) => setType(v as FeedbackType)}>
						<TabsList className="w-full">
							{TAB_CONFIG.map((tab) => (
								<TabsTrigger key={tab.value} value={tab.value} className="flex-1 gap-1.5">
									{tab.icon}
									{tab.label}
								</TabsTrigger>
							))}
						</TabsList>
					</Tabs>

					<div className="space-y-2">
						<Label htmlFor="feedback-title">Title</Label>
						<Input
							id="feedback-title"
							placeholder={type === "bug" ? "What went wrong?" : type === "feature" ? "What would you like?" : "Subject"}
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							autoFocus
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="feedback-description">Description</Label>
						<Textarea
							id="feedback-description"
							placeholder="Tell us more..."
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							rows={4}
						/>
					</div>

				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => handleOpenChange(false)}>
						Cancel
					</Button>
					<Button onClick={handleSubmit} disabled={submitting || !title.trim()}>
						{submitting ? "Sending..." : "Submit"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
